<template>
  <div class="kf-inline">
    <v-progress-linear
      v-if="loading"
      indeterminate
      height="2"
      color="primary"
      class="kf-inline__bar"
    />

    <div v-if="!rows.length" class="kf-inline__note">
      <template v-if="error">
        Couldn't load this borrower's messages.
        <v-btn small text color="primary" class="kf-inline__btn" @click="load">Retry</v-btn>
      </template>
      <template v-else-if="loading">Loading messages…</template>
      <template v-else>No messages under these filters.</template>
    </div>

    <template v-else>
      <table class="kf-inline__table">
        <colgroup>
          <col style="width: 200px" />
          <col />
          <col style="width: 110px" />
          <col style="width: 160px" />
          <col style="width: 170px" />
          <col style="width: 150px" />
          <col style="width: 50px" />
        </colgroup>
        <thead>
          <tr>
            <th>Received (IST)</th>
            <th>Message</th>
            <th>Channel</th>
            <th>Sent by</th>
            <th>Status when sent</th>
            <th>Outcome</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in visible" :key="item.log_id">
            <td class="kf-nowrap">{{ fmt.dateTimeIst(item.created) }}</td>
            <!-- The words the borrower got. The template's name is in the
                 message detail (eye), not here. -->
            <td>
              <div class="kf-message" :title="item.body">
                <div v-if="item.subject" class="kf-message__title">{{ item.subject }}</div>
                <div class="kf-message__body">{{ item.body || item.title }}</div>
              </div>
            </td>
            <td>
              <span class="kf-channel">
                <LucideIcon :name="channelIcon(item.channel)" :size="14" />
                {{ channelLabel(item.channel) }}
              </span>
            </td>
            <td class="kf-nowrap">{{ item.sender_label || "-" }}</td>
            <!-- The borrower's type when this message went out, saved on the
                 row then. Never today's: an older row says so instead. -->
            <td>
              <span v-if="item.borrower_type_label">{{ item.borrower_type_label }}</span>
              <span
                v-else
                class="kf-muted kf-nowrap"
                title="Sent before the borrower's status was recorded"
              >
                Not recorded
              </span>
            </td>
            <td>
              <StatusChip
                :status="item.outcome_label"
                :color="outcomeColor(item.outcome_group)"
                small
              />
            </td>
            <td class="kf-end">
              <v-btn icon small title="View message" @click="openLog(item)">
                <LucideIcon name="eye" :size="17" />
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="totalCount !== null && visible.length < totalCount" class="kf-inline__more">
        Showing {{ fmt.number(visible.length) }} of {{ fmt.number(totalCount) }}
        <template v-if="error">
          · Couldn't load more.
          <v-btn small text color="primary" class="kf-inline__btn" @click="fetchNext">
            Retry
          </v-btn>
        </template>
        <v-btn
          v-else
          small
          text
          color="primary"
          class="kf-inline__btn"
          :disabled="loading"
          @click="showMore"
        >
          Show {{ fmt.number(Math.min(STEP, totalCount - visible.length)) }} more
        </v-btn>
      </div>
    </template>

    <NotificationLogDialog v-model="logDialog" :log="selectedLog" />
  </div>
</template>

<script>
/**
 * Every message one borrower received, open under their User history row.
 *
 * Asks for the borrower's rows with the same filters the list used (date,
 * channel, static), so the total here always equals the row's "Received".
 * The tab opens every row on the page at once, so each list starts at FIRST
 * messages and grows by STEP, and the lists load a few at a time rather than
 * all twenty together.
 */
import StatusChip from "./StatusChip.vue";
import NotificationLogDialog from "./NotificationLogDialog.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { CHANNELS } from "@/app-js/karrofin/constants";

const OUTCOME_COLORS = {
  would_send: "#1976D2",
  provider_accepted: "#2E7D32",
};

/** Shown when the row opens. */
const FIRST = 5;
/** Added by each "Show more". */
const STEP = 10;
/**
 * Rows are fetched in pages of this size and shown from what has been
 * fetched. One fixed size keeps every page's offset lined up however many are
 * shown; the first page already covers FIRST, and each STEP needs one more.
 */
const PAGE = 10;

/* ------------------------------------------------------ request limit ---
   A page of the tab opens twenty borrowers at once. Their lists queue here
   and load MAX_PARALLEL at a time, so one visit cannot take every server
   worker. */
const MAX_PARALLEL = 4;
let running = 0;
const waiting = [];

function drain() {
  while (running < MAX_PARALLEL && waiting.length) {
    running += 1;
    waiting.shift()();
  }
}

function queued(task) {
  return new Promise((resolve, reject) => {
    waiting.push(() =>
      task()
        .then(resolve, reject)
        .finally(() => {
          running -= 1;
          drain();
        })
    );
    drain();
  });
}

export default {
  name: "KfUserMessagesInline",
  components: { StatusChip, NotificationLogDialog },
  props: {
    userId: { type: Number, required: true },
    /** The list's filters when it loaded: date_range, channel, static. */
    filters: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      fmt,
      STEP,
      loading: false,
      error: null,
      // Everything fetched so far, in pages of PAGE; `shown` of it is drawn.
      rows: [],
      shown: FIRST,
      totalCount: null,
      logDialog: false,
      selectedLog: null,
      ticket: 0,
    };
  },
  computed: {
    visible() {
      return this.rows.slice(0, this.shown);
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    // A closed row's queued request is skipped, and a late reply dropped.
    this.ticket += 1;
  },
  methods: {
    /** From the top: on open, and on Retry when nothing has loaded. */
    load() {
      this.ticket += 1;
      this.rows = [];
      this.shown = FIRST;
      this.totalCount = null;
      this.fetchNext();
    },
    showMore() {
      this.shown += STEP;
      this.fetchNext();
    },
    /** Fetches the next page if what is shown runs past what is fetched. */
    fetchNext() {
      const known = this.totalCount === null ? Infinity : this.totalCount;
      if (this.rows.length >= Math.min(this.shown, known)) return;
      const ticket = this.ticket;
      const params = {
        ...this.filters,
        user_id: this.userId,
        received: 1,
        page_number: this.rows.length / PAGE + 1,
        page_length: PAGE,
      };
      this.loading = true;
      this.error = null;
      queued(() =>
        ticket === this.ticket ? api.getAutomationHistory(params) : Promise.resolve(null)
      )
        .then((data) => {
          if (ticket !== this.ticket || !data) return;
          this.rows = this.rows.concat(data.results);
          this.totalCount = data.total_count;
          // Everything there is has arrived: never offer more than that.
          if (data.results.length < PAGE) this.totalCount = this.rows.length;
        })
        .catch((err) => {
          if (ticket !== this.ticket) return;
          this.error = err;
        })
        .finally(() => {
          if (ticket === this.ticket) this.loading = false;
        });
    },
    openLog(item) {
      this.selectedLog = item;
      this.logDialog = true;
    },
    channelLabel(value) {
      const c = CHANNELS.find((x) => x.value === value);
      return c ? c.label : value || "-";
    },
    channelIcon(value) {
      const c = CHANNELS.find((x) => x.value === value);
      return c ? c.icon : "send";
    },
    outcomeColor(group) {
      return OUTCOME_COLORS[group] || "";
    },
  },
};
</script>

<style scoped>
/* Lighter ground and a left rail tie the list to the borrower above, as on
   the Leads offer rows. The left padding lines it up with the User heading,
   past the 46px chevron column and the cell's own padding. */
.kf-inline {
  position: relative;
  padding: 4px 16px 8px 69px;
  background: var(--kf-surface-2);
  border-left: 3px solid #194e76;
  border-bottom: 1px solid var(--kf-border);
}
.kf-inline__bar {
  position: absolute;
  top: 0;
  left: 0;
}
.kf-inline__note,
.kf-inline__more {
  padding: 8px 0 2px;
  font-size: var(--kf-fs-meta);
  color: var(--kf-text-muted);
}
.kf-inline__btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}
.kf-inline__table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.kf-inline__table th {
  padding: 6px 12px 4px 0;
  text-align: left;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-text-muted);
  white-space: nowrap;
}
.kf-inline__table td {
  padding: 6px 12px 6px 0;
  font-size: var(--kf-fs-meta);
  color: #000;
  vertical-align: middle;
  border-top: 1px solid #e3ebf3;
}
.kf-inline__table tbody tr:hover > td {
  background: #e9f1f9;
}
.kf-nowrap {
  white-space: nowrap;
}
.kf-muted {
  color: #54636f;
}
.kf-end {
  text-align: right;
}
.kf-channel {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.kf-message {
  min-width: 0;
  line-height: 1.45;
}
.kf-message__title {
  font-weight: 600;
  color: #54636f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Two lines of the message; the whole text is on hover and in the eye. */
.kf-message__body {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #1f2d36;
}
</style>
